import { pageTitle } from '@palaccount/config';
import type { EChartsOption } from 'echarts';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableCell } from '@/components/ui/table';
import { formatMoney } from '@/lib/format';

import { Amount, Ltr, Pct } from '@/components/amount';
import { Chart } from '@/components/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import type { ReportConfigBody } from '@palaccount/contracts';

import { useWarehouses } from '@/features/inventory/stock-api';

import { usePriceTrend, usePurchaseReport } from './api';
import { configInForce } from './column-picker';
import { DetailTable, type DetailColumn } from './detail-table';
import { buildReportGrid, downloadXlsx } from './export';
import { PurchasedByFilter } from './purchased-by-filter';
import { DetailFold, MoreFilters, PRESETS, ReportToolbar, SummaryCards, useReportWindow, type PresetKey } from './report-shell';
import { ReportsTabs } from './reports-tabs';
import { WarehouseFilter } from './warehouse-filter';

type DetailRow = {
  billId: string;
  date: string;
  billNo: string;
  supplier: string;
  purchasedBy: string;
  warehouse: string;
  lines: number;
  /** The DOCUMENT's own amount, in `currency` (GEN-108). */
  total: string;
  currency: string;
  /** The same amount at that document's frozen rate — the addable one. */
  totalBase: string;
  status: 'paid' | 'partial' | 'overdue' | 'open';
};

const DEFAULT_COLUMNS = ['date', 'billNo', 'supplier', 'purchasedBy', 'warehouse', 'lines', 'total', 'currency', 'status'];

/**
 * DSH-06 — Purchase Reports: the sales mirror plus the two things buying
 * adds — the on-time % per supplier (BR-DSH-06-03) and the unit price trend
 * whose colour is INVERTED: a rising price renders red (BR-DSH-06-06).
 */
export function PurchaseReportPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  const { from, to, setFrom, setTo } = useReportWindow('thisYear');
  const [preset, setPreset] = useState<PresetKey>('thisYear');
  const [priceItemId, setPriceItemId] = useState<string | null>(null);
  const [columns, setColumns] = useState<string[]>(DEFAULT_COLUMNS);
  /** §5c: the trend metric switch — Spend or Orders, never both. */
  const [metric, setMetric] = useState<'spend' | 'orders'>('spend');
  const [drill, setDrill] = useState<string | null>(null);
  /** BR-DSH-10-17's filter. `null` is "All warehouses", the delivered default. */
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  /**
   * BR-DSH-06-11 — "The Purchased by filter must limit every figure on the
   * page." §5a row 4, `null` is "All", the delivered default (BR-DSH-10-09).
   * The limiting is the server's `created_by` predicate; this id rides BOTH
   * query keys below, which is what makes "every figure" true — the report's
   * cards, charts and table AND the drift card, together.
   */
  const [purchasedById, setPurchasedById] = useState<string | null>(null);
  /** BR-DSH-10-16 / DSH-10 Band 6 — the applied configuration (see DSH-05). */
  const [appliedConfig, setAppliedConfig] = useState<{ name: string; body: ReportConfigBody } | null>(null);

  // BR-DSH-10-07 — part of the query KEY, so every card, chart and table on
  // the page follows it, and so do the three exports (BR-DSH-10-24).
  const { data, isPending, isError } = usePurchaseReport({
    from,
    to,
    ...(warehouseId ? { warehouseId } : {}),
    ...(purchasedById ? { purchasedById } : {}),
  });
  /**
   * §5h's drift card, UNDER THE SAME FILTERS as everything above it.
   *
   * It used to take the window and the item and nothing else, which made it
   * the one figure-producing endpoint on this screen that no filter reached:
   * narrow the page to one warehouse and the card still plotted every
   * warehouse's purchases of the chosen item, and printed a drift percentage
   * measured across all of them. BR-DSH-10-07 is the rule — "changing any
   * filter must recompute every card, chart and table on the page at once" —
   * and BR-DSH-06-11 says the same thing about `Purchased by` in particular.
   *
   * WHAT MADE IT INVISIBLE: the item picker in the card's header is built
   * from `data.topItems`, which IS filtered. So the card offered a filtered
   * list of items and then plotted unfiltered data about whichever one was
   * chosen. Every name on screen was correct and the number underneath was
   * about a different population.
   *
   * Warehouse and Purchased by are the two of §5a's four dropdowns this
   * screen draws — see the note on `MoreFilters` below. The other two travel
   * the moment their pickers exist; the query and the server already take
   * them.
   */
  const priceTrend = usePriceTrend(
    priceItemId
      ? {
          from,
          to,
          itemId: priceItemId,
          ...(warehouseId ? { warehouseId } : {}),
          ...(purchasedById ? { purchasedById } : {}),
        }
      : null,
  );

  useEffect(() => {
    document.title = pageTitle(t('reports.tabPurchases'));
  }, [t]);

  useEffect(() => {
    if (!priceItemId && data?.topItems[0]?.id) setPriceItemId(data.topItems[0].id);
  }, [data, priceItemId]);

  const trendOption = useMemo<EChartsOption>(() => {
    const trend = data?.trend ?? [];
    return {
      grid: { left: 8, right: 8, top: 24, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: trend.map((m) => m.month) },
      yAxis: { type: 'value' },
      series: [
        {
          type: 'bar',
          data: trend.map((m) => (metric === 'spend' ? Number(m.spend) : m.bills)),
          itemStyle: { color: '#2563eb' },
          label: { show: true, position: 'top' },
        },
      ],
    };
  }, [data?.trend, metric]);

  const donutOption = useMemo<EChartsOption>(() => {
    const cats = data?.byCategory ?? [];
    return {
      tooltip: { trigger: 'item' },
      series: [{ type: 'pie', radius: ['45%', '75%'], data: cats.map((c) => ({ name: c.name, value: Number(c.spend) })) }],
    };
  }, [data?.byCategory]);

  const priceOption = useMemo<EChartsOption>(() => {
    const points = priceTrend.data?.points ?? [];
    return {
      grid: { left: 8, right: 8, top: 24, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: points.map((p) => p.date) },
      yAxis: { type: 'value', scale: true },
      series: [{ type: 'line', data: points.map((p) => Number(p.price)), label: { show: true, position: 'top' } }],
    };
  }, [priceTrend.data]);

  const detailColumns: DetailColumn<DetailRow>[] = [
    { id: 'date', label: t('reports.colDate'), render: (r) => <Ltr value={r.date} className="font-mono" /> },
    { id: 'billNo', label: t('reports.colBill'), render: (r) => <Ltr value={r.billNo} className="font-mono text-primary" /> },
    { id: 'supplier', label: t('reports.colSupplier'), render: (r) => r.supplier },
    { id: 'purchasedBy', label: t('reports.colPurchasedBy'), render: (r) => r.purchasedBy },
    { id: 'warehouse', label: t('reports.colWarehouse'), render: (r) => r.warehouse },
    { id: 'lines', label: t('reports.colItems'), numeric: true, render: (r) => r.lines },
    /*
     * THE ROW'S OWN CURRENCY, NOT THE REPORT'S. `total` is what the document
     * itself says (GEN-108), so rendering it against `baseCurrency` would
     * print "$45,900.00" on a euro invoice — a wrong LABEL in place of the
     * wrong TOTAL this sweep removed, which is the same defect in different
     * clothes. `Amount` takes the currency per row for exactly this case.
     */
    { id: 'total', label: t('reports.colTotal'), numeric: true, render: (r) => <Amount value={r.total} currency={r.currency} /> },
    /*
     * N-14's rule, applied to money instead of units: "one column, many units,
     * no header that could be true of the whole of it" — so the marker goes on
     * the ROW. On screen `Amount` already prints the symbol, but the XLSX and
     * PDF exports run every numeric cell through `bareNumber`, which strips it;
     * without this column a spreadsheet of the Total column would be mixed
     * currencies as bare numbers, which is F-27's defect in another medium.
     * Default-visible for that reason, and constant (and harmless) for the
     * single-currency company.
     */
    { id: 'currency', label: t('reports.colCurrency'), render: (r) => r.currency },
    /*
     * The converted figure, as its own column: this is the one the cards above
     * add (BR-DSH-05-09), so a reader who wants the rows to reconcile with the
     * headline can show it. Not in DEFAULT_COLUMNS — for a single-currency
     * company it is identical to the column beside it, and most companies are
     * that one. R13's picker is what makes an optional column the right shape.
     */
    { id: 'totalBase', label: t('reports.colTotalBase'), numeric: true, render: (r) => <Amount value={r.totalBase} currency={baseCurrency} /> },
    { id: 'status', label: t('reports.colStatus'), render: (r) => t(`reports.status_${r.status}`) },
  ];

  /** What is on screen, as one grid — Print, Excel and PDF all take it. */
  const rtl = i18n.language === 'ar';
  /**
   * BR-DSH-10-16 — the heading block's "filters applied" and the saved
   * configuration in force, exactly as on DSH-05 (see the sales page's
   * comment for the two mechanisms; this is the same pair on DSH-06).
   */
  const { data: warehouses } = useWarehouses({ status: 'active' });
  const warehouseInForce =
    (warehouseId && warehouses?.find((w) => w.id === warehouseId)?.name) || t('reports.allWarehouses');
  // BR-DSH-06-11 + BR-DSH-10-16: the filter that limits every figure is
  // named on every printed page too — a heading that listed only the
  // warehouse while the figures were narrowed to one buyer would state the
  // filters falsely exactly where nobody can hover to check.
  const purchaserInForce =
    (purchasedById && data?.purchasers.find((p) => p.id === purchasedById)?.name) || t('reports.all');
  const currentBody: ReportConfigBody = { columns, filters: {}, period: { preset, from, to } };
  const configName =
    appliedConfig !== null && configInForce(appliedConfig.body, currentBody) ? appliedConfig.name : null;
  const { grid, types } = buildReportGrid<DetailRow>({
    title: t('reports.tabPurchases'),
    from,
    to,
    filters: [
      { label: t('reports.filterWarehouse'), value: warehouseInForce },
      { label: t('reports.colPurchasedBy'), value: purchaserInForce },
    ],
    configName,
    cards: data
      ? [
          { label: t('reports.cardSpend'), value: formatMoney(data.summary.totalSpend.value, 'USD'), changePct: data.summary.totalSpend.changePct },
          { label: t('reports.cardBills'), value: String(data.summary.bills.value), changePct: data.summary.bills.changePct },
          { label: t('reports.cardAvgBill'), value: formatMoney(data.summary.avgBill.value, 'USD'), changePct: data.summary.avgBill.changePct },
          { label: t('reports.cardItemsPurchased'), value: String(data.summary.itemsPurchased.value), changePct: data.summary.itemsPurchased.changePct },
        ]
      : [],
    columnDefs: detailColumns,
    selected: columns,
    defaults: DEFAULT_COLUMNS,
    rows: data?.rows ?? [],
    value: (row, id) => (id === 'status' ? t(`reports.status_${row.status}`) : String(row[id as keyof DetailRow] ?? '')),
    dateColumns: ['date'],
  });

  const priceChange = priceTrend.data?.changePct ?? '0.0';
  const priceRose = priceChange.startsWith('+') && Number(priceChange) !== 0;
  const priceFell = priceChange.startsWith('-');

  return (
    <div className="flex flex-col gap-4">
      <ReportsTabs />
      <h1 className="text-xl font-semibold">{t('reports.tabPurchases')}</h1>

      <ReportToolbar
        // BR-DSH-10-02 — this report has a window and every figure covers it:
        // `from` and `to` ride in the query key below and reach the server.
        window={{ presets: PRESETS, preset, onPreset: setPreset, from, to, onFrom: setFrom, onTo: setTo }}
        grid={data ? grid : null}
        onExcel={() =>
          // BR-DSH-10-24: the same grid Print and PDF use.
          downloadXlsx(`purchase-report-${from}-${to}.xlsx`, t('reports.tabPurchases'), grid.columns, grid.rows, types, rtl)
        }
      />

      {/* BR-DSH-10-17 / ERR-DSH-10-03 — see `warehouse-filter.tsx`.
          BR-DSH-06-11 — see `purchased-by-filter.tsx`; §5a's Supplier and
          Category dropdowns are the two still missing from this band. */}
      <MoreFilters>
        <WarehouseFilter value={warehouseId} onChange={setWarehouseId} />
        <PurchasedByFilter
          options={data?.purchasers ?? []}
          value={purchasedById}
          onChange={setPurchasedById}
        />
        <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-2">{t('reports.filtersHint')}</p>
      </MoreFilters>

      {isPending ? (
        <div className="h-60 animate-pulse rounded-md bg-muted" aria-busy />
      ) : isError || !data ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">{t('common.loadFailed')}</p>
      ) : (
        <>
          <SummaryCards
            cards={[
              { label: t('reports.cardSpend'), value: data.summary.totalSpend.value, money: true, changePct: data.summary.totalSpend.changePct },
              { label: t('reports.cardBills'), value: String(data.summary.bills.value), changePct: data.summary.bills.changePct },
              { label: t('reports.cardAvgBill'), value: data.summary.avgBill.value, money: true, changePct: data.summary.avgBill.changePct },
              { label: t('reports.cardItemsPurchased'), value: String(data.summary.itemsPurchased.value), changePct: data.summary.itemsPurchased.changePct },
            ]}
          />

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">{t('reports.purchaseTrend')}</CardTitle>
              <span className="flex gap-1 text-xs">
                {(['spend', 'orders'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={cn('rounded border px-1.5 py-0.5', metric === m && 'border-primary text-primary')}
                    onClick={() => setMetric(m)}
                  >
                    {t(`reports.metric_${m}`)}
                  </button>
                ))}
              </span>
            </CardHeader>
            <CardContent>
              {data.trend.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
              ) : (
                <Chart option={trendOption} height={220} />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-[18px] lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.bySupplier')}</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-1 text-start">{t('reports.colSupplier')}</th>
                      <th className="pb-1 text-end">{t('reports.colSpend')}</th>
                      <th className="pb-1 text-end">{t('reports.colOrders')}</th>
                      <th className="pb-1 text-end">{t('reports.colAvg')}</th>
                      <th className="pb-1 text-end">{t('reports.colOnTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bySupplier.map((row, index) => (
                      <tr key={row.id ?? row.name} className={cn('border-t', index === 0 && 'bg-primary/5')}>
                        <TableCell className="px-0">{row.name}</TableCell>
                        <TableCell className="px-0 text-end font-medium"><Amount value={row.total} currency={baseCurrency} /></TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground">{row.bills}</TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground"><Amount value={row.avg} currency={baseCurrency} /></TableCell>
                        <TableCell className="px-0 text-end">
                          {row.onTimePct === null ? '—' : <Pct value={row.onTimePct} className="font-mono" />}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* §5e — the same donut as the sales side, over spend. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.spendByCategory')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.byCategory.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                ) : (
                  <>
                    <Chart option={donutOption} height={150} />
                    <ul className="mt-2 flex flex-col gap-1 text-sm">
                      {data.byCategory.map((cat) => {
                        const key = cat.id ?? cat.name;
                        return (
                          <li key={key}>
                            <button
                              type="button"
                              className={cn('flex w-full justify-between rounded px-1 hover:bg-muted', drill === key && 'bg-muted')}
                              onClick={() => setDrill(drill === key ? null : key)}
                            >
                              <span>{cat.name}</span>
                              <span className="text-muted-foreground">
                                <Pct value={cat.share} className="font-mono" />
                              </span>
                            </button>
                            {drill === key ? (
                              <ul className="ms-3 mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
                                {cat.items.map((item) => (
                                  <li key={item.name} className="flex justify-between">
                                    <span>{item.name}</span>
                                    <Amount value={item.spend} currency={baseCurrency} className="text-xs" />
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">{t('reports.drillHint')}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-[18px] lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.topPurchasedItems')}</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-1 text-start">{t('reports.colItem')}</th>
                      <th className="pb-1 text-end">{t('reports.colQty')}</th>
                      <th className="pb-1 text-end">{t('reports.colSpend')}</th>
                      <th className="pb-1 text-end">{t('reports.colAvgUnitCost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topItems.slice(0, 10).map((row) => (
                      <tr key={row.id ?? row.name} className="border-t">
                        <TableCell className="px-0">
                          <p>{row.name}</p>
                          <p className="font-mono text-xs text-muted-foreground"><Ltr value={row.code} /></p>
                        </TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground"><Ltr value={row.qty} className="font-mono" /></TableCell>
                        <TableCell className="px-0 text-end font-medium"><Amount value={row.spend} currency={baseCurrency} /></TableCell>
                        {/* BR-DSH-06-02: unit costs keep their cents. */}
                        <TableCell className="px-0 text-end"><Amount value={row.avgUnitCost} currency={baseCurrency} /></TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* §5g — the sales bar list, with the count read as orders. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.byWarehouse')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {data.byWarehouse.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                ) : (
                  data.byWarehouse.map((row) => {
                    const max = Math.max(...data.byWarehouse.map((w) => Number(w.value)), 1);
                    return (
                      <div key={row.name} className="flex items-center gap-2 text-sm">
                        <span className="w-32 truncate">{row.name}</span>
                        <div className="h-[22px] flex-1 rounded bg-muted">
                          <div className="h-full rounded bg-primary" style={{ width: `${(Number(row.value) / max) * 100}%` }} />
                        </div>
                        <Amount value={row.value} currency={baseCurrency} className="text-sm font-medium" />
                        <span className="text-xs text-muted-foreground">{t('reports.orderCount', { count: row.count })}</span>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">{t('reports.unitPriceTrend')}</CardTitle>
              <div className="flex items-center gap-2">
                {/* BR-DSH-06-06: the INVERTED tone — paying more is bad news. */}
                <bdi
                  dir="ltr"
                  className={cn(
                    'text-xs font-medium',
                    priceRose && 'text-red-600 dark:text-red-400',
                    priceFell && 'text-emerald-700 dark:text-emerald-400',
                    !priceRose && !priceFell && 'text-muted-foreground',
                  )}
                >
                  {t('unit.percent', { value: priceChange })}
                </bdi>
                <select
                  aria-label={t('reports.priceItem')}
                  className="rounded-md border bg-background px-2 py-1 text-xs"
                  value={priceItemId ?? ''}
                  onChange={(e) => setPriceItemId(e.target.value || null)}
                >
                  {data.topItems.map((item) => (
                    <option key={item.id ?? item.name} value={item.id ?? ''}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {(priceTrend.data?.points.length ?? 0) === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noPurchasesForItem')}</p>
              ) : (
                <Chart option={priceOption} height={160} />
              )}
            </CardContent>
          </Card>

          <DetailFold count={data.rows.length}>
            <DetailTable<DetailRow>
              report="purchase"
              columns={detailColumns}
              defaults={DEFAULT_COLUMNS}
              selected={columns}
              onSelect={setColumns}
              rows={data.rows}
              rowKey={(r) => r.billId}
              currentConfig={() => ({ columns, filters: {}, period: { preset, from, to } })}
              onApplyConfig={(config, name) => {
                if (config.period.from) setFrom(config.period.from);
                if (config.period.to) setTo(config.period.to);
                setPreset((config.period.preset as PresetKey) || 'custom');
                // BR-DSH-10-16: remember WHICH configuration, for the heading.
                setAppliedConfig({ name, body: config });
              }}
            />
          </DetailFold>
        </>
      )}
    </div>
  );
}
